/**
 * Предзагрузка раздела: опись запросов страницы и её повтор по намерению и в
 * простое (agroai-0e8.26, ADR-0020).
 *
 * Как это устроено
 * ----------------
 *
 * Страница не объявляет свои запросы руками: опись СНИМАЕТСЯ с неё самой.
 * Пока страница показывает первый экран (первые {@link RECORD_MS} после
 * навигации), каждый ответ, запомненный памятью ответов под версией
 * (`api/client.ts`), записывается в опись этой страницы — адрес и размер, без
 * данных. При наведении (фокусе, касании) на пункт меню опись этого адреса
 * повторяется: те же GET уходят ПАРАЛЛЕЛЬНО, даже если на самой странице они
 * шли цепочкой, и ложатся в ту же память ответов.
 *
 * Почему это не показывает старое
 * -------------------------------
 *
 * Предзагрузка ничего не рисует: она кладёт в память ответов кандидата с
 * версией, под которой его отдал сервер. Экран после навигации покажет его,
 * только если сервер на ЭТОЙ навигации подтвердил ту же версию
 * (`/data-version`, один лёгкий запрос). Сменилась отметка корпуса между
 * наведением и нажатием — версия другая, и экран берёт ответ заново под
 * скелетоном. Память чужого аккаунта не повторяется: опись ключуется тем, кто
 * вошёл, а версия включает участника.
 *
 * Почему опись, а не загрузчики маршрутов
 * ---------------------------------------
 *
 * Экраны Astra собраны из независимых блоков, у каждого свои запросы (40+
 * адаптеров). Загрузчик маршрута пришлось бы писать и сопровождать рядом с
 * каждым экраном, и он разошёлся бы с экраном при первой правке — ровно то,
 * от чего сервер ушёл, когда стал греть «что спрашивают», а не список,
 * написанный руками (`serving/warmup.py`). Новая страница получает
 * предзагрузку сама, первым же показом.
 */

import {
  API_BASE,
  inflightCount,
  onRemembered,
  prefetchUrl,
} from './client';
import { principalIdentity } from './session';

/** Окно описи после навигации: первый экран, а не поздние действия на странице. */
export const RECORD_MS = 8_000;

/** Задержка намерения: курсор, пролетевший над меню, не грузит ничего. */
export const INTENT_DELAY_MS = 80;

/** В простое не грузятся ответы крупнее этого (символов текста). */
export const IDLE_MAX_SIZE = 1_500_000;

/** Тишина после последнего запроса, прежде чем грузить в простое, мс. */
export const IDLE_QUIET_MS = 2_000;

/** Предел адресов в описи одной страницы. */
const MAX_URLS = 60;

const STORAGE_KEY = 'agroai.pageManifest.v1';

interface Manifest {
  /** День описи (местная дата): адреса несут `as_of=<сегодня>`. */
  day: string;
  urls: Array<[url: string, size: number]>;
}

type Book = Record<string, Manifest>;

let books: Map<string, Book> = new Map();
let current: { page: string; until: number; urls: Array<[string, number]> } | null = null;

function today(): string {
  const now = new Date();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${m}-${d}`;
}

function who(): string {
  return principalIdentity() ?? '';
}

function load(user: string): Book {
  const cached = books.get(user);
  if (cached) return cached;
  let book: Book = {};
  try {
    const raw = window.localStorage.getItem(`${STORAGE_KEY}:${user}`);
    if (raw) book = JSON.parse(raw) as Book;
  } catch {
    book = {};
  }
  books.set(user, book);
  return book;
}

function save(user: string, book: Book): void {
  try {
    window.localStorage.setItem(`${STORAGE_KEY}:${user}`, JSON.stringify(book));
  } catch {
    // Приватное окно или полное хранилище: опись живёт до перезагрузки.
  }
}

/** Ключ страницы: адрес и срез, с которыми на неё переходит меню. */
export function pageKey(pathname: string, search = ''): string {
  const query = new URLSearchParams(search);
  query.sort();
  const text = query.toString();
  return text ? `${pathname}?${text}` : pathname;
}

/** Навигация: дальнейшие ответы первого экрана пишутся в опись этой страницы. */
export function notePage(pathname: string, search = ''): void {
  const page = pageKey(pathname, search);
  if (current?.page === page) return;
  current = { page, until: Date.now() + RECORD_MS, urls: [] };
}

onRemembered((url, size) => {
  if (!current || Date.now() > current.until) return;
  if (!url.startsWith(API_BASE)) return;
  if (current.urls.length >= MAX_URLS) return;
  if (current.urls.some(([seen]) => seen === url)) return;
  current.urls.push([url, size]);
  // Опись переписывается каждым показом, но только когда новый показ хоть
  // что-то спросил: она — снимок того, что экран спрашивает СЕЙЧАС, а не
  // накопление всего, что он спрашивал когда-либо; пустой показ (ушли до
  // ответа) прежнюю опись не стирает.
  const user = who();
  const book = load(user);
  book[current.page] = { day: today(), urls: current.urls };
  save(user, book);
});

function manifestFor(pathname: string, search: string): Manifest | undefined {
  const book = load(who());
  const exact = book[pageKey(pathname, search)];
  const found = exact ?? book[pathname];
  if (!found || found.day !== today() || found.urls.length === 0) return undefined;
  return found;
}

/** Повторить опись страницы сейчас: все адреса параллельно. */
export function prefetchPage(pathname: string, search = ''): Promise<void> {
  const manifest = manifestFor(pathname, search);
  if (!manifest) return Promise.resolve();
  return Promise.all(manifest.urls.map(([url]) => prefetchUrl(url))).then(() => undefined);
}

/* ------------------------------------------------------------- намерение */

let intentTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Обработчики пункта меню: наведение и фокус — с задержкой намерения,
 * касание — сразу (на сенсорном экране наведения нет, а до нажатия
 * остаются сотни миллисекунд).
 */
export function intentHandlers(pathname: string, search = '') {
  const cancel = () => {
    if (intentTimer !== null) clearTimeout(intentTimer);
    intentTimer = null;
  };
  const later = () => {
    cancel();
    intentTimer = setTimeout(() => {
      intentTimer = null;
      void prefetchPage(pathname, search);
    }, INTENT_DELAY_MS);
  };
  return {
    onMouseEnter: later,
    onFocus: later,
    onMouseLeave: cancel,
    onBlur: cancel,
    onTouchStart: () => {
      cancel();
      void prefetchPage(pathname, search);
    },
  };
}

/* ---------------------------------------------------------------- простой */

let idleRun = 0;

/**
 * В простое — описи остальных пунктов меню, по одному адресу за раз и только
 * когда своих запросов в пути нет. Крупные ответы (карта, контуры) — только по
 * намерению: грузить мегабайты «на всякий случай» — платить чужим трафиком.
 * Новая навигация прерывает прежний проход.
 */
export function prefetchIdle(pages: Array<{ pathname: string; search?: string }>): () => void {
  const run = ++idleRun;
  const queue: string[] = [];
  const seen = new Set<string>();
  for (const page of pages) {
    const manifest = manifestFor(page.pathname, page.search ?? '');
    for (const [url, size] of manifest?.urls ?? []) {
      if (size > IDLE_MAX_SIZE || seen.has(url)) continue;
      seen.add(url);
      queue.push(url);
    }
  }
  let timer: ReturnType<typeof setTimeout> | null = null;
  const step = () => {
    timer = null;
    if (run !== idleRun || queue.length === 0) return;
    if (inflightCount() > 0) {
      timer = setTimeout(step, IDLE_QUIET_MS);
      return;
    }
    const url = queue.shift() as string;
    void prefetchUrl(url).then(() => {
      if (run === idleRun) timer = setTimeout(step, 50);
    });
  };
  timer = setTimeout(step, IDLE_QUIET_MS);
  return () => {
    if (run === idleRun) idleRun += 1;
    if (timer !== null) clearTimeout(timer);
  };
}

/** Только для тестов: забыть описи. */
export function resetPrefetchMemory(): void {
  books = new Map();
  current = null;
  try {
    for (const key of Object.keys(window.localStorage)) {
      if (key.startsWith(STORAGE_KEY)) window.localStorage.removeItem(key);
    }
  } catch {
    // нет хранилища — нечего забывать
  }
}
